<template>
  <div>
    <div
      v-if="error"
      class="alert alert-danger d-flex align-items-center gap-2 m-3"
    >
      <i class="bi bi-exclamation-triangle-fill" />
      <span>{{ error }}</span>
    </div>

    <div
      v-if="!statsLoading"
      class="wl-kpis"
    >
      <div class="wl-stat wl-stat--dark">
        <div class="wl-stat__head">
          <span class="wl-stat__title">{{ $t('components.infrastructure.fleetHealth.kpis.total') }}</span>
          <i class="bi bi-broadcast wl-stat__icon" />
        </div>
        <span class="wl-stat__value">{{ stats.total }}</span>
        <span class="wl-stat__meta">{{ $t('components.infrastructure.fleetHealth.kpis.totalMeta') }}</span>
      </div>

      <div class="wl-stat">
        <div class="wl-stat__head">
          <span class="wl-stat__title">{{ $t('components.infrastructure.fleetHealth.kpis.forecastable') }}</span>
          <i
            class="bi bi-graph-down-arrow wl-stat__icon"
            style="color:#4870b8"
          />
        </div>
        <span class="wl-stat__value">{{ stats.forecastable }}</span>
        <div
          class="wl-stat__accent"
          :style="{ width: stats.total ? `${(stats.forecastable / stats.total) * 100}%` : '0%', background: '#4870b8' }"
        />
        <span class="wl-stat__meta">{{ $t('components.infrastructure.fleetHealth.kpis.forecastableMeta') }}</span>
      </div>

      <div class="wl-stat">
        <div class="wl-stat__head">
          <span class="wl-stat__title">{{ $t('components.infrastructure.fleetHealth.kpis.urgent') }}</span>
          <i
            class="bi bi-exclamation-circle-fill wl-stat__icon"
            style="color:#ac4c3a"
          />
        </div>
        <span class="wl-stat__value">{{ stats.urgent }}</span>
        <div
          class="wl-stat__accent"
          :style="{ width: stats.total ? `${(stats.urgent / stats.total) * 100}%` : '0%', background: '#ac4c3a' }"
        />
        <span class="wl-stat__meta">{{ $t('components.infrastructure.fleetHealth.kpis.urgentMeta') }}</span>
      </div>
    </div>

    <div class="assets-grid-card">
      <div class="assets-grid-card__header">
        <div class="assets-grid-title">
          <div class="assets-grid-title__icon">
            <i class="bi bi-battery-half" />
          </div>
          <div>
            <div class="assets-grid-title__text">
              {{ $t('components.infrastructure.fleetHealth.title') }}
            </div>
            <div class="text-muted small">
              {{ $t('components.infrastructure.fleetHealth.subtitle') }}
            </div>
          </div>
        </div>
      </div>

      <div class="assets-grid-card__body">
        <div class="wl-assets-search-wrap">
          <input
            v-model="search"
            type="text"
            class="form-control wl-assets-search"
            :placeholder="$t('components.infrastructure.fleetHealth.searchPlaceholder')"
            @input="onSearchInput"
          >
        </div>

        <div
          v-if="hasActivated"
          class="wp-grid-wrap"
        >
          <div
            v-if="gridLoading"
            class="wp-loading wp-loading--overlay"
          >
            <span
              class="spinner-border wp-loading__spinner"
              role="status"
            />
            <span class="wp-loading__text">{{ $t('components.infrastructure.loading') }}</span>
          </div>

          <DxDataGrid
            ref="gridRef"
            class="ni-grid"
            :data-source="dataSource"
            :remote-operations="true"
            key-expr="assetId"
            :hover-state-enabled="true"
            :column-auto-width="true"
            :show-borders="false"
            :show-column-lines="false"
            :show-row-lines="false"
            :row-alternation-enabled="true"
            :column-hiding-enabled="isNarrowViewport"
            :word-wrap-enabled="false"
            :no-data-text="$t('components.infrastructure.fleetHealth.empty')"
          >
            <DxLoadPanel :enabled="false" />
            <DxPaging :page-size="10" />
            <DxPager
              :allowed-page-sizes="[10, 20, 50]"
              :show-info="true"
              :show-page-size-selector="true"
              :show-navigation-buttons="true"
            />

            <DxColumn
              data-field="name"
              :caption="$t('components.infrastructure.fleetHealth.columns.asset')"
              :min-width="130"
              :allow-sorting="false"
              cell-template="asset-template"
            />
            <DxColumn
              data-field="assetCategory"
              :caption="$t('components.infrastructure.fleetHealth.columns.category')"
              :min-width="110"
              :allow-sorting="false"
              cell-template="category-template"
            />
            <DxColumn
              data-field="gwId"
              :caption="$t('components.infrastructure.fleetHealth.columns.gwId')"
              :min-width="190"
              :allow-sorting="false"
              cell-template="gwid-template"
              :hiding-priority="2"
            />
            <DxColumn
              data-field="currentBatteryMv"
              :caption="$t('components.infrastructure.fleetHealth.columns.battery')"
              :min-width="100"
              alignment="center"
              :allow-sorting="false"
              cell-template="battery-template"
            />
            <DxColumn
              data-field="trendMvPerDay"
              :caption="$t('components.infrastructure.fleetHealth.columns.trend')"
              :min-width="100"
              alignment="center"
              :allow-sorting="false"
              cell-template="trend-template"
              :hiding-priority="1"
            />
            <DxColumn
              data-field="daysToLow"
              :caption="$t('components.infrastructure.fleetHealth.columns.daysToLow')"
              :min-width="150"
              alignment="center"
              :allow-sorting="false"
              cell-template="days-template"
            />
            <DxColumn
              data-field="etaLowBattery"
              :caption="$t('components.infrastructure.fleetHealth.columns.eta')"
              :min-width="140"
              :allow-sorting="false"
              cell-template="eta-template"
              :hiding-priority="0"
            />

            <template #asset-template="{ data }">
              <span>{{ data.data.name || data.data.serialNumber }}</span>
            </template>

            <template #category-template="{ data }">
              <span
                class="wl-category-badge"
                :class="`wl-category-badge--${data.data.assetCategory}`"
              >{{ categoryLabels[data.data.assetCategory] || data.data.assetCategory }}</span>
            </template>

            <template #gwid-template="{ data }">
              <span class="text-muted small font-monospace">{{ data.data.gwId }}</span>
            </template>

            <template #battery-template="{ data }">
              <span class="text-muted small font-monospace">{{ formatVoltage(data.data.currentBatteryMv) }}</span>
            </template>

            <template #trend-template="{ data }">
              <span class="text-muted small font-monospace">{{ formatTrend(data.data.trendMvPerDay) }}</span>
            </template>

            <template #days-template="{ data }">
              <span
                class="small font-monospace"
                :class="isUrgent(data.data.daysToLow) ? 'text-danger fw-bold' : 'text-muted'"
              >{{ data.data.daysToLow ?? '—' }}</span>
            </template>

            <template #eta-template="{ data }">
              <span class="text-muted small font-monospace">{{ formatEta(data.data.etaLowBattery) }}</span>
            </template>
          </DxDataGrid>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import CustomStore from 'devextreme/data/custom_store'
import {
  DxDataGrid,
  DxColumn,
  DxPaging,
  DxPager,
  DxLoadPanel,
} from 'devextreme-vue/data-grid'
import { getWirepasFleetHealth, getWirepasFleetHealthStats } from '@/crud/wirepas-location'
import { useNarrowViewport } from '@/composables/useNarrowViewport'
import { useDebouncedGridRefresh } from '@/composables/useDebouncedGridRefresh'
import { isWirepasFleetHealthUrgent, wirepasFmtVoltage, wirepasFmtTrend, wirepasFmtEta } from '@/utils/wirepas'

export default defineComponent({
  name: 'WirepasFleetHealthGrid',
  components: { DxDataGrid, DxColumn, DxPaging, DxPager, DxLoadPanel },
  props: {
    active: { type: Boolean as PropType<boolean>, default: false },
  },
  setup(props) {
    const { t } = useI18n()

    const error = ref<string | null>(null)
    const statsLoading = ref(true)
    const stats = ref({ total: 0, forecastable: 0, urgent: 0 })

    const gridLoading = ref(true)
    const search = ref('')
    const gridRef = ref<InstanceType<typeof DxDataGrid> | null>(null)

    const isNarrowViewport = useNarrowViewport()

    const categoryLabels: Record<string, string> = {
      pallet: t('components.wirepasLocations.assetCategories.pallet'),
      cart: t('components.wirepasLocations.assetCategories.cart'),
      box: t('components.wirepasLocations.assetCategories.box'),
      tag: t('components.wirepasLocations.assetCategories.tag'),
      sensor: t('components.wirepasLocations.assetCategories.sensor'),
    }

    const dataSource = new CustomStore({
      key: 'assetId',
      load: async (opts) => {
        gridLoading.value = true
        try {
          const params: any = { skip: opts.skip ?? 0, take: opts.take ?? 10 }
          if (search.value.trim()) params.searchValue = search.value.trim()
          const res = await getWirepasFleetHealth(params)
          return { data: res.data?.result ?? [], totalCount: res.data?.count ?? 0 }
        } catch (e: any) {
          error.value = e?.response?.data?.error || e?.message || t('components.infrastructure.error')
          throw e
        } finally {
          gridLoading.value = false
        }
      },
    })

    const fetchStats = async () => {
      try {
        const res = await getWirepasFleetHealthStats()
        stats.value = {
          total: res.data?.total ?? 0,
          forecastable: res.data?.forecastable ?? 0,
          urgent: res.data?.urgent ?? 0,
        }
      } catch {
        // KPI bar doesn't block the rest of the view
      } finally {
        statsLoading.value = false
      }
    }

    const hasActivated = ref(props.active)
    watch(() => props.active, (isActive) => {
      if (isActive) {
        hasActivated.value = true
        fetchStats()
      }
    }, { immediate: true })

    const onSearchInput = useDebouncedGridRefresh(gridRef)

    const isUrgent = isWirepasFleetHealthUrgent
    const formatVoltage = wirepasFmtVoltage
    const formatTrend = (mvPerDay: number | null) => wirepasFmtTrend(mvPerDay, t)
    const formatEta = wirepasFmtEta

    return {
      error,
      statsLoading,
      stats,
      hasActivated,
      gridLoading,
      search,
      gridRef,
      dataSource,
      isNarrowViewport,
      categoryLabels,
      onSearchInput,
      isUrgent,
      formatVoltage,
      formatTrend,
      formatEta,
    }
  },
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/wirepas-locations-view.scss';
</style>
